import React, { useState } from 'react';
import { Shield, People, CalendarMonth, Info, CheckCircle, PictureAsPdf, Loop } from '@mui/icons-material';
import './EPTWReport.css';

// Demo Report Data
const demoReportData = {
    month: "November 2025",
    reportDate: "December 5, 2025",

    safety: {
        statusFunnel: {
            created: 26,
            approved: 22,
            rejected: 4,
            closed: 20
        },
        certificates: {
            hotWork: 10,
            confinedSpace: 7,
            systemOverriding: 4,
            excavation: 5,
            isolation: 6,
            liftingHoisting: 3,
            radiation: 3
        },
        expiredPermits: [
            { permitNo: "421", daysOverdue: 18 },
            { permitNo: "428", daysOverdue: 15 }
        ],
        totalPermits: 26,
        totalCertificates: 38,
        complianceRate: 94.5
    },

    contractor: {
        manHours: {
            actual: 1780
        },
        contractors: [
            { name: "PIV Ventures Sdn Bhd", permits: 5, manHours: 320 },
            { name: "Mach3 Engineering Sdn Bhd", permits: 4, manHours: 280 },
            { name: "Life Force Communications Sdn Bhd", permits: 4, manHours: 240 },
            { name: "Sofiigreen Sdn Bhd", permits: 3, manHours: 210 },
            { name: "Tank Tech Engineering Sdn Bhd", permits: 3, manHours: 190 },
            { name: "KYROL Security Labs Sdn Bhd", permits: 2, manHours: 170 },
            { name: "Spectest Sdn Bhd", permits: 2, manHours: 150 },
            { name: "Arinex Sdn Bhd", permits: 2, manHours: 130 },
            { name: "Kalam Lestari", permits: 1, manHours: 90 }
        ],
        totalContractors: 9
    }
};

const EPTWReportsDashboard = () => {
    const [selectedMonth, setSelectedMonth] = useState('2025-11');
    const [loadingSafety, setLoadingSafety] = useState(false);
    const [loadingContractor, setLoadingContractor] = useState(false);

    const getSelectedMonthText = () => {
        const [year, month] = selectedMonth.split('-');
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        return `${monthNames[parseInt(month) - 1]} ${year}`;
    };

    const generateSafetyReport = async () => {
        setLoadingSafety(true);

        setTimeout(() => {
            try {
                if (!window.jspdf) {
                    throw new Error('jsPDF library not loaded. Please refresh the page.');
                }
                const { jsPDF } = window.jspdf;
                const doc = new jsPDF();

                const data = demoReportData.safety;
                const monthText = getSelectedMonthText();

                // Header with dark blue banner
                doc.setFillColor(30, 58, 138);
                doc.rect(0, 0, 210, 40, 'F');

                doc.setFontSize(20);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(255, 255, 255);
                doc.text('Safety Report', 105, 20, { align: 'center' });

                doc.setFontSize(10);
                doc.setFont(undefined, 'normal');
                doc.text(`Period: ${monthText}`, 105, 28, { align: 'center' });
                doc.text(`Generated: ${demoReportData.reportDate}`, 105, 34, { align: 'center' });

                doc.setTextColor(0, 0, 0);

                // Section 1: Status Funnel Overview
                let yPos = 45;
                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.text('Permit Status Funnel', 20, yPos);

                yPos += 10;

                // Vertical Bar Graph
                const chartX = 25;
                const chartY = yPos;
                const chartHeight = 70;
                const barWidth = 30;
                const gap = 10;

                const statuses = [
                    { label: 'Created', value: data.statusFunnel.created, color: [30, 58, 138] },
                    { label: 'Approved', value: data.statusFunnel.approved, color: [37, 99, 235] },
                    { label: 'Rejected', value: data.statusFunnel.rejected, color: [239, 68, 68] },
                    { label: 'Closed', value: data.statusFunnel.closed, color: [147, 197, 253] }
                ];

                const maxValue = Math.max(...statuses.map(s => s.value));
                const scale = chartHeight / maxValue;

                // Draw bars
                statuses.forEach((status, index) => {
                    const barHeight = status.value * scale;
                    const barX = chartX + index * (barWidth + gap);
                    const barY = chartY + chartHeight - barHeight;

                    doc.setFillColor(status.color[0], status.color[1], status.color[2]);
                    doc.rect(barX, barY, barWidth, barHeight, 'F');

                    doc.setDrawColor(20, 40, 100);
                    doc.setLineWidth(0.3);
                    doc.rect(barX, barY, barWidth, barHeight, 'S');

                    doc.setFontSize(8);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(0, 0, 0);
                    doc.text(status.value.toString(), barX + barWidth / 2, barY - 2, { align: 'center' });

                    doc.setFontSize(7);
                    doc.setFont(undefined, 'normal');
                    const labelLines = status.label.split(' ');
                    labelLines.forEach((line, i) => {
                        doc.text(line, barX + barWidth / 2, chartY + chartHeight + 5 + (i * 4), { align: 'center' });
                    });
                });

                // Draw axes
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.5);
                doc.line(chartX - 3, chartY, chartX - 3, chartY + chartHeight);
                doc.line(chartX - 3, chartY + chartHeight, chartX + 4 * (barWidth + gap), chartY + chartHeight);

                // Y-axis labels
                doc.setFontSize(7);
                const yLabels = 5;
                for (let i = 0; i <= yLabels; i++) {
                    const value = Math.round((maxValue / yLabels) * i);
                    const labelY = chartY + chartHeight - (chartHeight / yLabels) * i;
                    doc.text(value.toString(), chartX - 6, labelY + 1, { align: 'right' });
                    doc.setLineWidth(0.2);
                    doc.setDrawColor(200, 200, 200);
                    doc.line(chartX - 3, labelY, chartX + 4 * (barWidth + gap), labelY);
                }

                yPos = chartY + chartHeight + 18;

                // Status Funnel Table
                doc.autoTable({
                    startY: yPos,
                    head: [['Status', 'Count']],
                    body: [
                        ['Created', data.statusFunnel.created.toString()],
                        ['Approved', data.statusFunnel.approved.toString()],
                        ['Rejected', data.statusFunnel.rejected.toString()],
                        ['Closed', data.statusFunnel.closed.toString()]
                    ],
                    theme: 'grid',
                    headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: [239, 246, 255] },
                    margin: { left: 20, right: 20 }
                });

                yPos = doc.lastAutoTable.finalY + 15;

                // Section 2: Certificates Created
                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.text('Certificates Created by Type', 20, yPos);

                yPos += 5;

                doc.autoTable({
                    startY: yPos,
                    head: [['Certificate Type', 'Count']],
                    body: [
                        ['Hot Work', data.certificates.hotWork.toString()],
                        ['Confined Space', data.certificates.confinedSpace.toString()],
                        ['System Overriding', data.certificates.systemOverriding.toString()],
                        ['Excavation', data.certificates.excavation.toString()],
                        ['Isolation', data.certificates.isolation.toString()],
                        ['Lifting & Hoisting', data.certificates.liftingHoisting.toString()],
                        ['Radiation', data.certificates.radiation.toString()],
                        ['TOTAL', data.totalCertificates.toString()]
                    ],
                    theme: 'grid',
                    headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: [239, 246, 255] },
                    margin: { left: 20, right: 20 },
                    columnStyles: {
                        0: { cellWidth: 90 },
                        1: { cellWidth: 40, halign: 'center' }
                    },
                    didParseCell: function(data) {
                        if (data.row.index === 7) {
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fillColor = [191, 219, 254];
                        }
                    }
                });

                // Add new page for Expired Permits
                doc.addPage();
                yPos = 20;

                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.text('Expired Permits', 20, yPos);

                yPos += 5;

                const expiredTableBody = data.expiredPermits.map(permit => [
                    permit.permitNo,
                    permit.daysOverdue.toString()
                ]);

                doc.autoTable({
                    startY: yPos,
                    head: [['Permit Number', 'Days Overdue']],
                    body: expiredTableBody,
                    theme: 'grid',
                    headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: [239, 246, 255] },
                    margin: { left: 20, right: 20 },
                    columnStyles: {
                        0: { cellWidth: 80, halign: 'center' },
                        1: { cellWidth: 60, halign: 'center' }
                    }
                });

                // Footer
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setTextColor(128);
                    doc.text(`Safety Report - ${demoReportData.month}`, 20, 285);
                    doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: 'right' });
                    doc.text('Oriens e-PTW', 105, 285, { align: 'center' });
                }

                doc.save(`Safety_Report_${demoReportData.month.replace(' ', '_')}.pdf`);

            } catch (error) {
                console.error('Error generating Safety Report:', error);
                alert('Error generating report. Please try again.');
            } finally {
                setLoadingSafety(false);
            }
        }, 500);
    };

    const generateContractorReport = async () => {
        setLoadingContractor(true);

        setTimeout(() => {
            try {
                if (!window.jspdf) {
                    throw new Error('jsPDF library not loaded. Please refresh the page.');
                }
                const { jsPDF } = window.jspdf;
                const doc = new jsPDF();

                const data = demoReportData.contractor;
                const monthText = getSelectedMonthText();

                // Header with dark blue banner
                doc.setFillColor(30, 58, 138);
                doc.rect(0, 0, 210, 40, 'F');

                doc.setFontSize(20);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(255, 255, 255);
                doc.text('Contractor Report', 105, 20, { align: 'center' });

                doc.setFontSize(10);
                doc.setFont(undefined, 'normal');
                doc.text(`Period: ${monthText}`, 105, 28, { align: 'center' });
                doc.text(`Generated: ${demoReportData.reportDate}`, 105, 34, { align: 'center' });

                doc.setTextColor(0, 0, 0);

                // Section 1: Permits by Contractor
                let yPos = 45;
                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.text('Permits by Contractor', 20, yPos);

                yPos += 10;

                // Vertical Bar Chart
                const chartX = 20;
                const chartY = yPos;
                const chartWidth = 170;
                const chartHeight = 80;
                const barWidth = 16;
                const gap = 3;

                const maxValue = Math.max(...data.contractors.map(c => c.permits));
                const scale = chartHeight / maxValue;

                // Draw bars
                data.contractors.forEach((contractor, index) => {
                    const barHeight = contractor.permits * scale;
                    const barX = chartX + index * (barWidth + gap);
                    const barY = chartY + chartHeight - barHeight;

                    doc.setFillColor(30, 58, 138);
                    doc.rect(barX, barY, barWidth, barHeight, 'F');

                    doc.setDrawColor(20, 40, 100);
                    doc.setLineWidth(0.3);
                    doc.rect(barX, barY, barWidth, barHeight, 'S');

                    doc.setFontSize(8);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(0, 0, 0);
                    doc.text(contractor.permits.toString(), barX + barWidth / 2, barY - 2, { align: 'center' });

                    doc.setFontSize(6);
                    doc.setFont(undefined, 'normal');
                    const nameWords = contractor.name.split(' ');
                    const shortName = nameWords[0].substring(0, 5).toUpperCase();
                    doc.text(shortName, barX + barWidth / 2, chartY + chartHeight + 5, { align: 'center' });
                });

                // Draw axes
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.5);
                doc.line(chartX - 2, chartY, chartX - 2, chartY + chartHeight);
                doc.line(chartX - 2, chartY + chartHeight, chartX + data.contractors.length * (barWidth + gap), chartY + chartHeight);

                // Y-axis labels
                doc.setFontSize(7);
                const yLabels = 5;
                for (let i = 0; i <= yLabels; i++) {
                    const value = Math.round((maxValue / yLabels) * i);
                    const labelY = chartY + chartHeight - (chartHeight / yLabels) * i;
                    doc.text(value.toString(), chartX - 5, labelY + 1, { align: 'right' });
                    doc.setLineWidth(0.1);
                    doc.setDrawColor(200, 200, 200);
                    doc.line(chartX - 2, labelY, chartX + data.contractors.length * (barWidth + gap), labelY);
                }

                yPos = chartY + chartHeight + 15;

                // Section 2: Contractor Details Table
                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.text('Contractor Details', 20, yPos);

                yPos += 5;

                const tableBody = data.contractors.map(contractor => [
                    contractor.name,
                    contractor.permits.toString(),
                    contractor.manHours.toLocaleString()
                ]);

                const totalPermits = data.contractors.reduce((sum, c) => sum + c.permits, 0);
                const totalManHours = data.contractors.reduce((sum, c) => sum + c.manHours, 0);
                tableBody.push([
                    'TOTAL',
                    totalPermits.toString(),
                    totalManHours.toLocaleString()
                ]);

                doc.autoTable({
                    startY: yPos,
                    head: [['Contractor Name', 'Permits', 'Man-Hours']],
                    body: tableBody,
                    theme: 'grid',
                    headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: [239, 246, 255] },
                    margin: { left: 20, right: 20 },
                    columnStyles: {
                        0: { cellWidth: 90 },
                        1: { cellWidth: 40, halign: 'center' },
                        2: { cellWidth: 50, halign: 'right' }
                    },
                    didParseCell: function(data) {
                        if (data.row.index === tableBody.length - 1) {
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fillColor = [191, 219, 254];
                        }
                    }
                });

                // Footer
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setTextColor(128);
                    doc.text(`Contractor Report - ${demoReportData.month}`, 20, 285);
                    doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: 'right' });
                    doc.text('Oriens e-PTW', 105, 285, { align: 'center' });
                }

                doc.save(`Contractor_Report_${demoReportData.month.replace(' ', '_')}.pdf`);

            } catch (error) {
                console.error('Error generating Contractor Report:', error);
                alert('Error generating report. Please try again.');
            } finally {
                setLoadingContractor(false);
            }
        }, 500);
    };

    return (
        <div className="eptw-dashboard">
            <div className="eptw-container">
                {/* Header */}
                <div className="eptw-header">
                    <h1>e-PTW Reports Dashboard</h1>
                    <p>Generate comprehensive reports for Safety, Contractor, and Audit metrics</p>
                </div>

                {/* Filter Section */}
                <div className="eptw-filter-section">
                    <div className="eptw-filter-content">
                        <div className="eptw-filter-input-group">
                            <CalendarMonth className="eptw-filter-icon" sx={{ fontSize: 28 }} />
                            <div className="eptw-filter-label-wrapper">
                                <label htmlFor="monthFilter">Report Period</label>
                                <select
                                    id="monthFilter"
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    className="eptw-filter-select"
                                >
                                    <option value="2025-01">January 2025</option>
                                    <option value="2025-02">February 2025</option>
                                    <option value="2025-03">March 2025</option>
                                    <option value="2025-04">April 2025</option>
                                    <option value="2025-05">May 2025</option>
                                    <option value="2025-06">June 2025</option>
                                    <option value="2025-07">July 2025</option>
                                    <option value="2025-08">August 2025</option>
                                    <option value="2025-09">September 2025</option>
                                    <option value="2025-10">October 2025</option>
                                    <option value="2025-11">November 2025</option>
                                    <option value="2025-12">December 2025</option>
                                </select>
                            </div>
                        </div>
                        <div className="eptw-filter-info">
                            <Info className="eptw-filter-info-icon" sx={{ fontSize: 18 }} />
                            Select a month to filter report data
                        </div>
                    </div>
                </div>

                {/* Cards Grid */}
                <div className="eptw-cards-grid">
                    {/* Safety Report Card */}
                    <div className="eptw-report-card">
                        <div className="eptw-card-header">
                            <div className="eptw-card-icon-wrapper green">
                                <Shield sx={{ fontSize: 36, color: '#059669' }} />
                            </div>
                            <h2 className="eptw-card-title">Safety Report</h2>
                        </div>
                        <p className="eptw-card-description">
                            Comprehensive analysis of permit statuses, certificates breakdown, and expired permits tracking.
                        </p>
                        <ul className="eptw-card-features">
                            <li>
                                <CheckCircle className="eptw-feature-icon green" sx={{ fontSize: 18 }} />
                                Status Funnel Analysis
                            </li>
                            <li>
                                <CheckCircle className="eptw-feature-icon green" sx={{ fontSize: 18 }} />
                                Vertical Bar Chart
                            </li>
                            <li>
                                <CheckCircle className="eptw-feature-icon green" sx={{ fontSize: 18 }} />
                                Certificates Breakdown
                            </li>
                            <li>
                                <CheckCircle className="eptw-feature-icon green" sx={{ fontSize: 18 }} />
                                Expired Permits Tracking
                            </li>
                        </ul>
                        <button
                            onClick={generateSafetyReport}
                            disabled={loadingSafety}
                            className="eptw-generate-btn green"
                        >
                            {loadingSafety ? (
                                <>
                                    <Loop className="eptw-btn-icon spin" sx={{ fontSize: 22 }} />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <PictureAsPdf className="eptw-btn-icon" sx={{ fontSize: 22 }} />
                                    Generate Safety Report
                                </>
                            )}
                        </button>
                    </div>

                    {/* Contractor Report Card */}
                    <div className="eptw-report-card">
                        <div className="eptw-card-header">
                            <div className="eptw-card-icon-wrapper blue">
                                <People sx={{ fontSize: 36, color: '#2563eb' }} />
                            </div>
                            <h2 className="eptw-card-title">Contractor Report</h2>
                        </div>
                        <p className="eptw-card-description">
                            Detailed breakdown of contractor activities, man-hours, and utilization metrics.
                        </p>
                        <ul className="eptw-card-features">
                            <li>
                                <CheckCircle className="eptw-feature-icon blue" sx={{ fontSize: 18 }} />
                                Permits by Contractor
                            </li>
                            <li>
                                <CheckCircle className="eptw-feature-icon blue" sx={{ fontSize: 18 }} />
                                Vertical Bar Chart
                            </li>
                            <li>
                                <CheckCircle className="eptw-feature-icon blue" sx={{ fontSize: 18 }} />
                                Man-Hours & Details Table
                            </li>
                        </ul>
                        <button
                            onClick={generateContractorReport}
                            disabled={loadingContractor}
                            className="eptw-generate-btn blue"
                        >
                            {loadingContractor ? (
                                <>
                                    <Loop className="eptw-btn-icon spin" sx={{ fontSize: 22 }} />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <PictureAsPdf className="eptw-btn-icon" sx={{ fontSize: 22 }} />
                                    Generate Contractor Report
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="eptw-footer">
                    <p>© 2025 Oriens e-PTW | Report generation system</p>
                </div>
            </div>
        </div>
    );
};

export default EPTWReportsDashboard;